import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import HeaderMenu from "../../component/Navbar/headermenu";
import CustomFooter from "../../component/Footer/customFooter";
import { SolutionPageStructuredData } from "../../config/StructuredData";

const CAFirmsSolutionPage = () => (
  <>
    <SolutionPageStructuredData
      name="Workflow Management Software for CA Firms in India"
      description="Simplifyte helps CA firms manage GST, income tax, audit, TDS, ROC, advisory workflows with client-wise work tracking, FY/period tracking, document collection, review notes, partner approvals and closure proof."
      url="https://www.simplifyte.com/solutions/ca-firms"
    />
    <HeaderMenu />

    <main style={{ paddingTop: "80px", minHeight: "100vh" }}>
      {/* Hero */}
      <section style={{ background: "linear-gradient(135deg,#093A5B 0%,#0a4d6e 100%)", padding: "60px 24px", color: "#fff", textAlign: "center" }}>
        <div style={{ maxWidth: "860px", margin: "0 auto" }}>
          <p style={{ fontSize: "13px", letterSpacing: "2px", textTransform: "uppercase", opacity: 0.7, marginBottom: "16px" }}>Solutions / CA Firms</p>
          <h1 style={{ fontSize: "clamp(28px,4vw,48px)", fontWeight: 700, lineHeight: 1.2, marginBottom: "20px" }}>
            Workflow Management Software for CA Firms in India
          </h1>
          <p style={{ fontSize: "18px", opacity: 0.85, maxWidth: "700px", margin: "0 auto 32px" }}>
            Simplifyte helps CA firms manage GST, income tax, audit, TDS, ROC and advisory work with client-wise tracking, FY/period workflows, document collection, review notes, partner approvals and closure proof — replacing WhatsApp, Excel and email chaos.
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/contactsales" style={{ background: "#fff", color: "#093A5B", padding: "12px 28px", borderRadius: "8px", fontWeight: 600, textDecoration: "none" }}>Book a Demo</Link>
            <Link to="/pricing" style={{ border: "1px solid rgba(255,255,255,0.5)", color: "#fff", padding: "12px 28px", borderRadius: "8px", fontWeight: 600, textDecoration: "none" }}>View Pricing</Link>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section style={{ padding: "60px 24px", background: "#f8fafc" }}>
        <div style={{ maxWidth: "860px", margin: "0 auto" }}>
          <h2 style={{ fontSize: "28px", fontWeight: 700, color: "#093A5B", marginBottom: "16px" }}>
            The problem most CA firms face
          </h2>
          <p style={{ fontSize: "16px", color: "#475569", lineHeight: 1.7, marginBottom: "24px" }}>
            Most CA firms track work through a combination of WhatsApp messages, Excel sheets, and verbal follow-ups. This works when the team is small — but breaks down as clients and staff grow.
          </p>
          <ul style={{ listStyle: "none", padding: 0, display: "grid", gap: "12px" }}>
            {[
              "No single place to see which client's GST, ITR, audit or TDS work is in which stage",
              "Staff reminders happen on WhatsApp — no structure, no accountability",
              "Documents arrive over email, WhatsApp, and Drive — never in one place",
              "Partners have no real-time visibility into team workload or pending approvals",
              "When something goes wrong, there is no audit trail to trace back",
              "Closing a file has no formal proof — it just gets marked done verbally",
            ].map((item, i) => (
              <li key={i} style={{ display: "flex", gap: "12px", alignItems: "flex-start", background: "#fff", padding: "14px 16px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                <span style={{ color: "#ef4444", marginTop: "2px", flexShrink: 0 }}>✕</span>
                <span style={{ color: "#475569", fontSize: "15px" }}>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Solution */}
      <section style={{ padding: "60px 24px" }}>
        <div style={{ maxWidth: "860px", margin: "0 auto" }}>
          <h2 style={{ fontSize: "28px", fontWeight: 700, color: "#093A5B", marginBottom: "16px" }}>
            How Simplifyte solves it for CA firms
          </h2>
          <p style={{ fontSize: "16px", color: "#475569", marginBottom: "32px", lineHeight: 1.7 }}>
            Simplifyte is not generic task software adapted for CA use. It is built around the actual workflows of CA firms — client identity, financial year, period, document status, review, approval, and closure proof all exist natively.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: "20px" }}>
            {[
              { title: "Client-wise work tracking", desc: "Every GST return, ITR, audit, TDS or ROC task is linked to a specific client and tracked from assignment to closure." },
              { title: "FY and period tracking", desc: "Tag work to AY 2025-26, Q1, Q2, or monthly periods so no filing period is ever missed or duplicated." },
              { title: "Document collection", desc: "Track exactly which documents are pending from which client. Automated reminders replace manual WhatsApp follow-ups." },
              { title: "Review and approval workflow", desc: "Staff submits → reviewer checks → partner approves. Every step is logged with a timestamp." },
              { title: "Deadline reminders", desc: "Automated reminders for GST, ITR, TDS, ROC and other filing deadlines go to the right person at the right time." },
              { title: "Audit trail and closure proof", desc: "Complete log of every action. Timestamped closure proof for every completed and delivered file." },
            ].map((f, i) => (
              <div key={i} style={{ background: "#f8fafc", padding: "20px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <CheckCircle2 size={22} color="#093A5B" style={{ marginBottom: "10px" }} />
                <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b", marginBottom: "8px" }}>{f.title}</h3>
                <p style={{ fontSize: "14px", color: "#64748b", lineHeight: 1.6 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before / With */}
      <section style={{ padding: "60px 24px", background: "#f8fafc" }}>
        <div style={{ maxWidth: "860px", margin: "0 auto" }}>
          <h2 style={{ fontSize: "28px", fontWeight: 700, color: "#093A5B", marginBottom: "32px", textAlign: "center" }}>Before Simplifyte / With Simplifyte</h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
            <div style={{ background: "#fff5f5", padding: "24px", borderRadius: "10px" }}>
              <h3 style={{ color: "#ef4444", marginBottom: "16px", fontSize: "16px", fontWeight: 600 }}>Before Simplifyte</h3>
              {["GST deadlines tracked in Excel or memory","Documents collected over WhatsApp","Partner reviews done verbally or on call","No proof of what was filed or delivered","Staff handover is a phone call","Missed deadlines are nobody's fault"].map((t,i)=>(
                <p key={i} style={{ fontSize: "14px", color: "#64748b", marginBottom: "10px", paddingLeft: "8px", borderLeft: "2px solid #fca5a5" }}>{t}</p>
              ))}
            </div>
            <div style={{ background: "#f0fdf4", padding: "24px", borderRadius: "10px" }}>
              <h3 style={{ color: "#16a34a", marginBottom: "16px", fontSize: "16px", fontWeight: 600 }}>With Simplifyte</h3>
              {["All GST, ITR, audit work tracked by client and period","Documents collected with automated reminders","Partner reviews and approvals in the system","Timestamped closure proof for every file","Staff handover is one click","Deadline ownership is clear and logged"].map((t,i)=>(
                <p key={i} style={{ fontSize: "14px", color: "#64748b", marginBottom: "10px", paddingLeft: "8px", borderLeft: "2px solid #86efac" }}>{t}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "60px 24px", textAlign: "center", background: "#093A5B", color: "#fff" }}>
        <h2 style={{ fontSize: "28px", fontWeight: 700, marginBottom: "16px" }}>Ready to bring order to your CA firm's workflow?</h2>
        <p style={{ opacity: 0.8, marginBottom: "28px", fontSize: "16px" }}>See how Simplifyte works for CA firms — book a 20-minute demo.</p>
        <Link to="/contactsales" style={{ background: "#fff", color: "#093A5B", padding: "14px 32px", borderRadius: "8px", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "8px" }}>
          Book a Demo <ArrowRight size={16} />
        </Link>
      </section>
    </main>

    <CustomFooter />
  </>
);

export default CAFirmsSolutionPage;
